import React, { useEffect, useState, useCallback, useMemo } from "react";
import {Link, useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

// Import Icons
import FeatherIcon from "feather-icons-react";

// Import Components
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
  FormGroup,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import BootstrapTheme from "@fullcalendar/bootstrap";
import Flatpickr from "react-flatpickr";
import 'flatpickr/dist/flatpickr.min.css';
import listPlugin from '@fullcalendar/list';
import SimpleBar from "simplebar-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import TableContainer from '../../Components/Common/TableContainer';
import api,{handleApiError} from "../../helpers/api";
import {useDispatch} from "react-redux";

// Function to calculate luminance and determine readable text color
const getReadableTextColor = (hexColor) => {
  const r = parseInt(hexColor.slice(1, 3), 16) / 255;
  const g = parseInt(hexColor.slice(3, 5), 16) / 255;
  const b = parseInt(hexColor.slice(5, 7), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Custom CSS (unchanged)
const customStyles = `
  .calendar-card, .meetings-table-card, .sidebar-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: radial-gradient(circle at 20% 20%, rgba(255, 107, 53, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(14, 165, 233, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 60%, rgba(0, 72, 142, 0.1) 0%, transparent 50%);
  }
  .calendar-modal .modal-content {
    border-radius: 8px;
    border: none;
  }
  .calendar-modal .modal-header {
    background: radial-gradient(circle at 20% 20%, rgba(255, 107, 53, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(14, 165, 233, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 60%, rgba(0, 72, 142, 0.1) 0%, transparent 50%);
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding: 1.5rem;
  }
  .calendar-modal .modal-header h5 {
    font-weight: 600;
    font-size: 1.4rem;
    color: #01448c;
  }
  .calendar-modal .modal-body {
    padding: 1.5rem;
    background-color: #f9fafb;
  }
  .calendar-modal .form-control, .calendar-modal .form-select {
    border-radius: 4px;
    border: 1px solid #ced4da;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }
  .calendar-modal .form-control:focus, .calendar-modal .form-select:focus {
    border-color: #0ab39c;
    box-shadow: 0 0 0 0.2rem rgba(10, 179, 156, 0.25);
  }
  .calendar-modal .form-label {
    font-weight: 500;
    color: #2a4966;
  }
  .calendar-modal .form-check-label {
    font-size: 0.9rem;
    color: #2a4966;
  }
  .calendar-modal .form-check-input {
    border-color: #ced4da;
  }
  .calendar-modal .form-check-input:checked {
    background-color: #0ab39c;
    border-color: #0ab39c;
  }
  .meetings-table {
    width: 100%;
    max-width: 100%;
    table-layout: auto;
  }
  .meetings-table th, .meetings-table td {
    vertical-align: middle;
    padding: 0.75rem;
    position: relative;
    overflow: visible !important;
  }
  .meetings-table tr:hover {
    background-color: #f1f5f7;
  }
  .meetings-table tr.selected {
    background-color: #e6f7f5;
  }
  .sidebar-card .card-header, .calendar-card .card-header, .meetings-table-card .card-header {
    color: #ffffff;
    padding: 1rem 1.5rem;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  .sidebar-card .card-body, .calendar-card .card-body, .meetings-table-card .card-body {
    padding: 1.5rem;
    background-color: #f9fafb;
  }
  .action-btn {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
    transition: background-color 0.3s ease, transform 0.2s ease;
  }
  .action-btn:hover {
    transform: translateY(-2px);
  }
  .card-header {
    background-color: transparent;
    border-bottom: 1px solid #e9ecef;
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .card-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2a4966;
    text-align: center;
    margin: 0;
  }
  .dropdown {
    position: relative;
    display: inline-block;
  }
  .dropdown-menu {
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: none;
    position: absolute !important;
    z-index: 1000;
    min-width: 120px;
    transform: translateX(-50%);
    left: 50%;
    top: 100%;
    margin-top: 5px;
  }
  .dropdown-item {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
    color: #2a4966;
  }
  .dropdown-item:hover {
    background-color: #f1f5f7;
    color: #0ab39c;
  }
  .dropdown-item i {
    margin-right: 0.5rem;
  }
  .fc-event-selected {
    border: 2px solid #0ab39c !important;
    background-color: #e6f7f5 !important;
  }
  .formation-color-indicator {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 0.5rem;
  }
  .calendar-container {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    flex-wrap: nowrap;
    align-items: stretch;
  }
  .calendar-card, .sidebar-card {
    flex: 1;
    min-width: 0;
  }
  .calendar-card {
    flex: 3;
  }
  .sidebar-card {
    flex: 1;
    max-width: 300px;
  }
  .table-container {
    margin-top: 1rem;
  }
  @media (max-width: 992px) {
    .calendar-container {
      flex-direction: column;
      flex-wrap: wrap;
    }
    .sidebar-card {
      max-width: 100%;
      margin-top: 1rem;
    }
  }
  @media (max-width: 768px) {
    .calendar-modal .modal-body {
      padding: 1rem;
    }
    .meetings-table-card {
      margin-top: 1rem;
    }
  }
`;

const Calender = () => {
  const { t } = useTranslation();
  document.title = t('calendar.title');
const dispatch = useDispatch();
    const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [inscriptions, setInscriptions] = useState([]);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [meetingForm, setMeetingForm] = useState({
    formationId: '',
    title: '',
    platform: 'Zoom',
    hostEmail: '',
    date: new Date().setHours(new Date().getHours() + 1),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
    participantIds: [],
    requireAuthentication: false,
    waitingRoom: false,
    recurrenceType: 'One-Time',
    repeatInterval: 1,
    weeklyDays: '',
    monthlyDay: 1,
    password: '',
    duration: 60,
  });
  const [meetingLoading, setMeetingLoading] = useState(false);

  const fetchMeetings = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/formation/meeting/meetings', {
        withCredentials: true,
      });
      // Assign formation colors to meetings
      const formationColors = [
        '#ff6b35', // Orange
        '#0ea5e9', // Sky blue
        '#00448c', // Dark blue
        '#10b981', // Green
        '#8b5cf6', // Purple
        '#f43f5e', // Red
      ];
      const formationColorMap = {};
      console.log('Fetched meetings:', response.data.data);
      response.data.data.forEach(meeting => {
        if (meeting.formation?._id && !formationColorMap[meeting.formation._id]) {
          formationColorMap[meeting.formation._id] = formationColors[Object.keys(formationColorMap).length % formationColors.length];
        }
        meeting.formationColor = formationColorMap[meeting.formation._id] || '#0ab39c';
      });
      setMeetings(response.data.data);
      setIsLoading(false);
    } catch (err) {
      if (err.response.data?.status === 401 || err.response.data?.status === 403) {
        await handleApiError(err, dispatch, navigate);
      } else {
        toast.error(t('calendar.toasts.fetch_meetings_error'));
        setIsLoading(false);
      }
    }
  }, [t, dispatch, navigate]);
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/user?role=admin,editor', {
        withCredentials: true,
      });
      setUsers(response.data.users);
    } catch (err) {
      if (err.response?.data.status === 401 || err.response?.data.status === 403) {
        await handleApiError(err, dispatch, navigate);
      } else {
        toast.error(t('calendar.toasts.fetch_users_error'));
      }
    }
  }, [t, dispatch, navigate]);
  // Fetch inscriptions for selected meeting
  const fetchInscriptions = useCallback(async (participantIds, formationId) => {
    try {
      const response = await api.get(`/formation/${formationId}/inscriptions`, {
        withCredentials: true,
      });
      const meetingInscriptions = response.data.data.filter(inscription =>
          participantIds.includes(inscription._id.toString())
      );
      setInscriptions(meetingInscriptions);
    } catch (err) {
      if (err.response.data?.status === 401 || err.response.data?.status === 403) {
        await handleApiError(err, dispatch, navigate);
      } else {
        toast.error(t('calendar.toasts.fetch_inscriptions_error'));
      }
    }
  }, [t, dispatch, navigate]);
  useEffect(() => {
    fetchMeetings();
    fetchUsers();
  }, [fetchMeetings, fetchUsers]);

  // Handle copy to clipboard
  const handleCopyLink = (meetingLink) => {
    if (!meetingLink || typeof meetingLink !== 'string') {
      console.error('Invalid meeting link:', meetingLink);
      toast.error(t('calendar.toasts.copy_link_invalid'));
      return;
    }
    navigator.clipboard.write(meetingLink)
        .then(() => toast.info(t('calendar.toasts.copy_link_success')))
        .catch(err => {
          console.error('Failed to copy link:', err);
          toast.error(t('calendar.toasts.copy_link_error'));
        });
  };

  // Generate calendar events with recurrence
  const generateCalendarEvents = useMemo(() => {
    const events = [];
    // Color palette for formations
    const formationColors = [
      '#ff6b35', // Orange
      '#0ea5e9', // Sky blue
      '#00448c', // Dark blue
      '#10b981', // Green
      '#8b5cf6', // Purple
      '#f43f5e', // Red
    ];
    // Map formation IDs to colors
    const formationColorMap = {};
    meetings.forEach(meeting => {
      if (meeting.formation?._id && !formationColorMap[meeting.formation._id]) {
        formationColorMap[meeting.formation._id] = formationColors[Object.keys(formationColorMap).length % formationColors.length];
      }
    });

    meetings.forEach(meeting => {
      const start = new Date(meeting.date);
      const end = new Date(start.getTime() + meeting.duration * 60 * 1000);
      const formationColor = formationColorMap[meeting.formation?._id] || '#0ab39c';
      const baseEvent = {
        id: meeting._id,
        title: meeting.title,
        start,
        end,
        backgroundColor: formationColor,
        borderColor: formationColor,
        textColor: getReadableTextColor(formationColor),
        classNames: [`formation-color-${meeting.formation?._id || 'default'}`],
        extendedProps: {
          formationId: meeting.formation._id,
          formationTitle: meeting.formation.titre,
          host: meeting.host,
          participants: meeting.participants,
          platform: meeting.platform,
          meetingLink: meeting.meetingLink,
          password: meeting.password,
          requireAuthentication: meeting.requireAuthentication,
          waitingRoom: meeting.waitingRoom,
          recurrence: meeting.recurrence,
          zoomMeetingId: meeting.zoomMeetingId,
          formationColor: formationColor,
        },
      };

      if (meeting.recurrence && meeting.recurrence.type !== 'One-Time') {
        const { type, endDate, repeatInterval, weeklyDays, monthlyDay } = meeting.recurrence;
        const endRecurrence = new Date(endDate);
        let currentDate = new Date(start);

        while (currentDate <= endRecurrence) {
          if (type === 'Daily') {
            events.push({ ...baseEvent, start: new Date(currentDate), end: new Date(currentDate.getTime() + meeting.duration * 60 * 1000) });
            currentDate.setDate(currentDate.getDate() + repeatInterval);
          } else if (type === 'Weekly') {
            const days = weeklyDays.split(',').map(Number);
            for (let i = 0; i < 7; i++) {
              const dayDate = new Date(currentDate.getTime() + i * 24 * 60 * 60 * 1000);
              if (days.includes(dayDate.getDay() + 1) && dayDate <= endRecurrence) {
                events.push({ ...baseEvent, start: new Date(dayDate), end: new Date(dayDate.getTime() + meeting.duration * 60 * 1000) });
              }
            }
            currentDate.setDate(currentDate.getDate() + 7 * repeatInterval);
          } else if (type === 'Monthly') {
            const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + repeatInterval, monthlyDay);
            if (nextMonth <= endRecurrence) {
              events.push({ ...baseEvent, start: new Date(nextMonth), end: new Date(nextMonth.getTime() + meeting.duration * 60 * 1000) });
            }
            currentDate.setMonth(currentDate.getMonth() + repeatInterval);
          }
        }
      } else {
        events.push(baseEvent);
      }
    });

    // Inject dynamic CSS for each formation color
    const dynamicStyles = Object.entries(formationColorMap).map(([formationId, color]) => `
      .formation-color-${formationId} .fc-event, 
      .formation-color-${formationId} .fc-event-dot {
        background-color: ${color} !important;
        border-color: ${color} !important;
      }
      .formation-color-${formationId} .fc-event-title {
        color: ${getReadableTextColor(color)} !important;
      }
    `).join('\n');

    // Append dynamic styles to the document
    const styleSheet = document.createElement('style');
    styleSheet.innerText = dynamicStyles;
    document.head.appendChild(styleSheet);

    return events;
  }, [meetings]);

  // Generate upcoming recurrence dates
  const getUpcomingRecurrence = (recurrence, startDate, duration) => {
    if (!recurrence || recurrence.type === 'One-Time') return [];
    const dates = [];
    const start = new Date(startDate);
    const endRecurrence = new Date(recurrence.endDate);
    let currentDate = new Date(start);
    const maxDates = 5;

    while (currentDate <= endRecurrence && dates.length < maxDates) {
      if (recurrence.type === 'Daily') {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + recurrence.repeatInterval);
      } else if (recurrence.type === 'Weekly') {
        const days = recurrence.weeklyDays.split(',').map(Number);
        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(currentDate.getTime() + i * 24 * 60 * 60 * 1000);
          if (days.includes(dayDate.getDay() + 1) && dayDate <= endRecurrence && dates.length < maxDates) {
            dates.push(new Date(dayDate));
          }
        }
        currentDate.setDate(currentDate.getDate() + 7 * recurrence.repeatInterval);
      } else if (recurrence.type === 'Monthly') {
        const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + recurrence.repeatInterval, recurrence.monthlyDay);
        if (nextMonth <= endRecurrence && dates.length < maxDates) {
          dates.push(new Date(nextMonth));
        }
        currentDate.setMonth(currentDate.getMonth() + recurrence.repeatInterval);
      }
    }
    return dates.map(date => ({
      start: date,
      end: new Date(date.getTime() + duration * 60 * 1000),
    }));
  };

  // Handle modal toggle
  const toggle = () => {
    setModal(!modal);
    if (modal) {
      setIsEdit(false);
      setMeetingForm({
        formationId: '',
        title: '',
        platform: 'Zoom',
        hostEmail: '',
        date: new Date().setHours(new Date().getHours() + 1),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        participantIds: [],
        requireAuthentication: false,
        waitingRoom: false,
        recurrenceType: 'One-Time',
        repeatInterval: 1,
        weeklyDays: '',
        monthlyDay: 1,
        password: '',
        duration: 60,
      });
    }
  };

  // Handle event click
  const handleEventClick = (arg) => {
    const event = arg.event;
    setSelectedMeeting({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      formationId: event.extendedProps.formationId,
      formationTitle: event.extendedProps.formationTitle,
      host: event.extendedProps.host,
      participants: event.extendedProps.participants,
      platform: event.extendedProps.platform,
      meetingLink: event.extendedProps.meetingLink,
      password: event.extendedProps.password,
      requireAuthentication: event.extendedProps.requireAuthentication,
      waitingRoom: event.extendedProps.waitingRoom,
      recurrence: event.extendedProps.recurrence,
      zoomMeetingId: event.extendedProps.zoomMeetingId,
      formationColor: event.extendedProps.formationColor,
    });
    fetchInscriptions(event.extendedProps.participants, event.extendedProps.formationId);
  };

  // Handle table row click
  const handleTableRowClick = (meeting) => {
    setSelectedMeeting({
      id: meeting._id,
      title: meeting.title,
      start: new Date(meeting.date),
      end: new Date(new Date(meeting.date).getTime() + meeting.duration * 60 * 1000),
      formationId: meeting.formation._id,
      formationTitle: meeting.formation.titre,
      host: meeting.host,
      participants: meeting.participants,
      platform: meeting.platform,
      meetingLink: meeting.meetingLink,
      password: meeting.password,
      requireAuthentication: meeting.requireAuthentication,
      waitingRoom: meeting.waitingRoom,
      recurrence: meeting.recurrence,
      zoomMeetingId: meeting.zoomMeetingId,
      formationColor: meeting.formationColor,
    });
    fetchInscriptions(meeting.participants, meeting.formation._id);
  };

  // Handle delete
  const handleDeleteMeeting = async () => {
    try {
      await api.delete(`/formation/meeting/meetings/${selectedMeeting._id}`, {
        withCredentials: true,
      });
      toast.success(t('calendar.toasts.delete_success'));
      setDeleteModal(false);
      setSelectedMeeting(null);
      fetchMeetings();
    } catch (err) {
      if (err.response.data?.status === 401 || err.response.data?.status === 403) {
        await handleApiError(err, dispatch, navigate);
      } else {
        toast.error(t('calendar.toasts.delete_error'));
      }
    }
  };

  // Handle edit click
  const handleEditClick = (meeting) => {
    setIsEdit(true);
    setMeetingForm({
      formationId: meeting.formationId,
      title: meeting.title,
      platform: meeting.platform,
      hostEmail: meeting.host.email,
      date: meeting.start,
      endDate: meeting.recurrence ? meeting.recurrence.endDate : new Date(new Date().setMonth(new Date().getMonth() + 1)),
      participantIds: meeting.participants,
      requireAuthentication: meeting.requireAuthentication,
      waitingRoom: meeting.waitingRoom,
      recurrenceType: meeting.recurrence ? meeting.recurrence.type : 'One-Time',
      repeatInterval: meeting.recurrence ? meeting.recurrence.repeatInterval : 1,
      weeklyDays: meeting.recurrence && meeting.recurrence.weeklyDays ? meeting.recurrence.weeklyDays : '',
      monthlyDay: meeting.recurrence && meeting.recurrence.monthlyDay ? meeting.recurrence.monthlyDay : 1,
      password: meeting.password || '',
      duration: meeting.duration,
    });
    setModal(true);
  };

  // Handle meeting form changes
  const handleMeetingFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMeetingForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDateChange = (name, date) => {
    setMeetingForm(prev => ({ ...prev, [name]: date[0] }));
  };

  // Handle create/edit meeting
  const handleSubmitMeeting = async () => {
    try {
      setMeetingLoading(true);
      const endpoint = isEdit ? `http://localhost:8000/formation/meeting/meetings/:id${selectedMeeting.id}` : 'http://localhost:8000/formation/meetings';
      const method = isEdit ? 'patch' : 'post';
      const response = await axios[method](endpoint, {
        formationId: meetingForm.formationId,
        title: meetingForm.title,
        platform: meetingForm.platform,
        date: meetingForm.date,
        endDate: meetingForm.recurrenceType !== 'One-Time' ? meetingForm.endDate : undefined,
        participantIds: meetingForm.participantIds,
        hostEmail: meetingForm.hostEmail,
        requireAuthentication: meetingForm.requireAuthentication,
        waitingRoom: meetingForm.waitingRoom,
        recurrenceType: meetingForm.recurrenceType,
        repeatInterval: meetingForm.repeatInterval,
        weeklyDays: meetingForm.weeklyDays,
        monthlyDay: meetingForm.monthlyDay,
        password: meetingForm.password,
        duration: meetingForm.duration,
      }, { withCredentials: true });

      const toastContent = (
          <div>
            {t(`calendar.toasts.${isEdit ? 'update_success' : 'create_success'}`)} <a href={response.data.meeting.join_url} target="_blank" rel="noopener noreferrer">{t('calendar.toasts.join_link')}</a>
            <Button
                color="primary"
                size="sm"
                className="ms-2"
                onClick={() => handleCopyLink(response.data.meeting.join_url)}
            >
              {t('calendar.toasts.copy_link')}
            </Button>
            {response.data.meeting.emailList && response.data.meeting.emailList.length > 0 && (
                <div className="mt-2">
                  {t('calendar.toasts.invited_emails')}:
                  <ul>
                    {response.data.meeting.emailList.map(email => (
                        <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
            )}
          </div>
      );
      toast.success(toastContent, { autoClose: 5000 });
      setModal(false);
      setSelectedMeeting(null);
      fetchMeetings();
    } catch (err) {
      toast.error(err.response?.data?.message || t('calendar.toasts.save_error'));
    } finally {
      setMeetingLoading(false);
    }
  };

  // Table columns
  const columns = useMemo(() => [
    {
      Header: t('calendar.table.formation'),
      accessor: 'formation.titre',
      Cell: ({ value, row }) => (
          <span>
          <span
              className="formation-color-indicator"
              style={{ backgroundColor: row.original.formationColor || '#0ab39c' }}
          ></span>
            {value || '-'}
        </span>
      ),
    },
    {
      Header: t('calendar.table.meeting_title'),
      accessor: 'title',
      Cell: ({ value }) => <span>{value}</span>,
    },
    {
      Header: t('calendar.table.host'),
      accessor: 'host',
      Cell: ({ value }) => <span>{value ? `${value.firstname} ${value.lastname}` : '-'}</span>,
    },
    {
      Header: t('calendar.table.date'),
      accessor: 'date',
      Cell: ({ value }) => <span>{new Date(value).toLocaleString()}</span>,
    },
    {
      Header: t('calendar.table.recurrence'),
      accessor: 'recurrence',
      Cell: ({ value }) => (
          <span className={`badge badge-soft-${value ? 'success' : 'info'}`}>
          {value ? `${t(`calendar.recurrence.${value.type.toLowerCase()}`)}${value.repeatInterval > 1 ? ` ${t('calendar.recurrence.every')} ${value.repeatInterval} ${t(`calendar.recurrence.${value.type.toLowerCase()}s`)}` : ''}${value.type === 'Weekly' ? ` ${t('calendar.recurrence.on')} ${value.weeklyDays}` : value.type === 'Monthly' ? ` ${t('calendar.recurrence.on_day')} ${value.monthlyDay}` : ''}` : t('calendar.recurrence.one_time')}
        </span>
      ),
    },
    {
      Header: t('calendar.table.platform'),
      accessor: 'platform',
      Cell: ({ value }) => <span className="badge badge-soft-success">{value}</span>,
    },
    {
      Header: t('calendar.table.actions'),
      Cell: ({ row }) => (
          <Dropdown
              isOpen={dropdownOpen[row.original._id] || false}
              toggle={() => setDropdownOpen(prev => ({ ...prev, [row.original._id]: !prev[row.original._id] }))}
          >
            <DropdownToggle tag="button" className="btn btn-soft-secondary action-btn">
              <i className="ri-more-2-fill"></i>
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => handleTableRowClick(row.original)}>
                <i className="ri-eye-fill me-1"></i> {t('calendar.actions.view')}
              </DropdownItem>
              <DropdownItem onClick={() => handleEditClick(row.original)}>
                <i className="ri-pencil-fill me-1"></i> {t('calendar.actions.edit')}
              </DropdownItem>
              <DropdownItem onClick={() => { setSelectedMeeting(row.original); setDeleteModal(true); }}>
                <i className="ri-delete-bin-fill me-1"></i> {t('calendar.actions.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
      ),
    },
  ], [dropdownOpen, t]);

  return (
      <React.Fragment>
        <style>{customStyles}</style>
        <div className="page-content">
          <Container fluid>
            <BreadCrumb title={t('calendar.title')} pageTitle={t('calendar.page_title')} />
            <ToastContainer closeButton={false} limit={1} />
            <Row className="calendar-container">
              <Col xl={selectedMeeting ? 9 : 12} className="calendar-card-col">
                <Card className="calendar-card">
                  <CardHeader>
                    <h5 className="card-title">{t('calendar.calendar_title')}</h5>
                  </CardHeader>
                  <CardBody>
                    {isLoading ? (
                        <div className="text-center py-4">
                          <i className="ri-loader-2-line fs-24 text-primary spin"></i>
                        </div>
                    ) : (
                        <FullCalendar
                            plugins={[BootstrapTheme, dayGridPlugin, interactionPlugin, listPlugin]}
                            initialView="dayGridMonth"
                            slotDuration={"00:15:00"}
                            handleWindowResize={true}
                            themeSystem="bootstrap"
                            headerToolbar={{
                              left: "prev,next today",
                              center: "title",
                              right: "dayGridMonth,dayGridWeek,dayGridDay,listWeek",
                            }}
                            events={generateCalendarEvents}
                            editable={false}
                            selectable={true}
                            eventClick={handleEventClick}
                            eventClassNames={selectedMeeting ? (event) => event.id === selectedMeeting.id ? 'fc-event-selected' : '' : ''}
                        />
                    )}
                  </CardBody>
                </Card>
              </Col>
              {selectedMeeting && (
                  <Col xl={3} className="sidebar-card-col">
                    <Card className="sidebar-card">
                      <CardHeader>
                        <h5 className="mb-0">{t('calendar.sidebar.title')}</h5>
                      </CardHeader>
                      <CardBody>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.title_label')}</h6>
                          <p>{selectedMeeting.title}</p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.formation')}</h6>
                          <p>
                        <span
                            className="formation-color-indicator"
                            style={{ backgroundColor: selectedMeeting.formationColor || '#0ab39c' }}
                        ></span>
                            {selectedMeeting.formationTitle}
                          </p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.host')}</h6>
                          <p>{selectedMeeting.host ? `${selectedMeeting.host.firstname} ${selectedMeeting.host.lastname} (${selectedMeeting.host.email})` : '-'}</p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.date_time')}</h6>
                          <p>{new Date(selectedMeeting.start).toLocaleString()}</p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.duration')}</h6>
                          <p>Duration: {selectedMeeting.duration} minutes</p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.platform')}</h6>
                          <span className="badge badge-soft-success">{selectedMeeting.platform}</span>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.password')}</h6>
                          <p>{selectedMeeting.password || t('calendar.sidebar.password_none')}</p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.settings')}</h6>
                          <p>
                        <span className={`badge badge-soft-${selectedMeeting.requireAuthentication ? 'success' : 'secondary'} me-1`}>
                          {t('calendar.sidebar.authentication')}: {t(`calendar.sidebar.authentication_${selectedMeeting.requireAuthentication ? 'on' : 'off'}`)}
                        </span>
                            <span className={`badge badge-soft-${selectedMeeting.waitingRoom ? 'success' : 'secondary'}`}>
                          {t('calendar.sidebar.waiting_room')}: {t(`calendar.sidebar.waiting_room_${selectedMeeting.waitingRoom ? 'on' : 'off'}`)}
                        </span>
                          </p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.join_link')}</h6>
                          <p>
                            {selectedMeeting.meetingLink && typeof selectedMeeting.meetingLink === 'string' ? (
                                <>
                                  <a href={selectedMeeting.meetingLink} target="_blank" rel="noopener noreferrer">{t('calendar.sidebar.join_meeting')}</a>
                                  <Button
                                      color="primary"
                                      size="sm"
                                      className="ms-2"
                                      onClick={() => handleCopyLink(selectedMeeting.meetingLink)}
                                  >
                                    {t('calendar.sidebar.copy')}
                                  </Button>
                                </>
                            ) : (
                                <span>{t('calendar.sidebar.no_link')}</span>
                            )}
                          </p>
                        </div>
                        <div className="mb-3">
                          <h6 className="fw-semibold">{t('calendar.sidebar.inscriptions')}</h6>
                          <SimpleBar style={{ maxHeight: '200px' }}>
                            {inscriptions.length > 0 ? (
                                <ul className="list-unstyled">
                                  {inscriptions.map(inscription => (
                                      <li key={inscription._id}>{inscription.emailClient}</li>
                                  ))}
                                </ul>
                            ) : (
                                <p>{t('calendar.sidebar.no_inscriptions')}</p>
                            )}
                          </SimpleBar>
                        </div>
                        {selectedMeeting.recurrence && selectedMeeting.recurrence.type !== 'One-Time' && (
                            <div className="mb-3">
                              <h6 className="fw-semibold">{t('calendar.sidebar.upcoming_recurrence')}</h6>
                              <SimpleBar style={{ maxHeight: '150px' }}>
                                {getUpcomingRecurrence(selectedMeeting.recurrence, selectedMeeting.start, selectedMeeting.duration).map((date, index) => (
                                    <p key={index}>{new Date(date.start).toLocaleString()}</p>
                                ))}
                              </SimpleBar>
                            </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
              )}
            </Row>
            <Row className="table-container">
              <Col xl={12}>
                <Card className="meetings-table-card">
                  <CardHeader>
                    <h5 className="card-title">{t('calendar.table.title')}</h5>
                  </CardHeader>
                  <CardBody>
                    <TableContainer
                        columns={columns}
                        data={meetings}
                        isGlobalFilter={false}
                        customPageSize={8}
                        className="custom-header-css"
                        divClass="table-responsive table-card mb-1"
                        tableClass="align-middle table-nowrap meetings-table"
                        theadClass="table-light text-muted"
                        thClass=""
                        trClass={(row) => selectedMeeting && selectedMeeting.id === row._id ? 'selected' : ''}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Modal isOpen={modal} id="meeting-modal" centered className="calendar-modal" fade>
              <ModalHeader toggle={toggle} className="p-3">
                {t(`calendar.modal.${isEdit ? 'edit_meeting' : 'create_meeting'}`)}
              </ModalHeader>
              <ModalBody>
                <Row>
                  <Col md={6} className="mb-3">
                    <label className="form-label" id="formation-tooltip">
                      <i className="ri-information-line me-1"></i> {t('calendar.form.formation')}
                    </label>
                    <UncontrolledTooltip placement="top" target="formation-tooltip">
                      {t('calendar.form.formation_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="select"
                        name="formationId"
                        value={meetingForm.formationId}
                        onChange={handleMeetingFormChange}
                        className="form-select"
                        disabled={meetingLoading || isEdit}
                    >
                      <option value="">{t('calendar.form.formation_select')}</option>
                      {[...new Set(meetings.map(m => m.formation._id))].map(id => {
                        const formation = meetings.find(m => m.formation._id === id)?.formation;
                        return <option key={id} value={id}>{formation?.titre}</option>;
                      })}
                    </Input>
                  </Col>
                  <Col md={6} className="mb-3">
                    <label className="form-label" id="title-tooltip">
                      <i className="ri-information-line me-1"></i> {t('calendar.form.meeting_title')}
                    </label>
                    <UncontrolledTooltip placement="top" target="title-tooltip">
                      {t('calendar.form.meeting_title_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="text"
                        name="title"
                        value={meetingForm.title}
                        onChange={handleMeetingFormChange}
                        className="form-control"
                        placeholder={t('calendar.form.meeting_title_placeholder')}
                        maxLength={200}
                        disabled={meetingLoading}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <label className="form-label" id="platform-tooltip">
                      <i className="ri-information-line me-1"></i> {t('calendar.form.platform')}
                    </label>
                    <UncontrolledTooltip placement="top" target="platform-tooltip">
                      {t('calendar.form.platform_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="select"
                        name="platform"
                        value={meetingForm.platform}
                        onChange={handleMeetingFormChange}
                        className="form-select"
                        disabled={meetingLoading}
                    >
                      <option value="Zoom">{t('calendar.form.platform_zoom')}</option>
                      <option value="GoogleMeet" disabled>{t('calendar.form.platform_google_meet')}</option>
                    </Input>
                  </Col>
                  <Col md={6} className="mb-3">
                    <label className="form-label" id="host-tooltip">
                      <i className="ri-information-line me-1"></i> {t('calendar.form.host')}
                    </label>
                    <UncontrolledTooltip placement="top" target="host-tooltip">
                      {t('calendar.form.host_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="select"
                        name="hostEmail"
                        value={meetingForm.hostEmail}
                        onChange={handleMeetingFormChange}
                        className="form-select"
                        disabled={meetingLoading}
                    >
                      <option value="">{t('calendar.form.host_select')}</option>
                      {users?.map(user => (
                          <option key={user._id} value={user.email}>
                            {user.firstname} {user.lastname}
                          </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={6} className="mb-3">
                    <label className="form-label" id="duration-tooltip">
                      <i className="ri-time-line me-1"></i> {t('calendar.form.duration')}
                    </label>
                    <UncontrolledTooltip placement="top" target="duration-tooltip">
                      {t('calendar.form.duration_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="number"
                        name="duration"
                        value={meetingForm.duration}
                        onChange={handleMeetingFormChange}
                        className="form-control"
                        placeholder={t('calendar.form.duration_placeholder')}
                        min="1"
                        disabled={meetingLoading}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <label className="form-label mb-0" id="start-date-tooltip">
                      <i className="ri-calendar-line me-1"></i> {t('calendar.form.start_date')}
                    </label>
                    <UncontrolledTooltip placement="top" target="start-date-tooltip">
                      {t('calendar.form.start_date_tooltip')}
                    </UncontrolledTooltip>
                    <Flatpickr
                        className="form-control"
                        options={{
                          enableTime: true,
                          dateFormat: "Y-m-d H:i",
                          minDate: '2025-07-04T14:43:00Z',
                          defaultDate: meetingForm.date,
                        }}
                        value={meetingForm.date}
                        onChange={(date) => handleDateChange('date', date)}
                        disabled={meetingLoading}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <label className="form-label" id="password-tooltip">
                      <i className="ri-lock-line me-1"></i> {t('calendar.form.password')}
                    </label>
                    <UncontrolledTooltip placement="top" target="password-tooltip">
                      {t('calendar.form.password_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="text"
                        name="password"
                        value={meetingForm.password}
                        onChange={handleMeetingFormChange}
                        className="form-control"
                        placeholder={t('calendar.form.password_placeholder')}
                        maxLength={10}
                        disabled={meetingLoading}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <label className="form-label" id="recurrence-tooltip">
                      <i className="ri-repeat-line me-1"></i> {t('calendar.form.recurrence')}
                    </label>
                    <UncontrolledTooltip placement="top" target="recurrence-tooltip">
                      {t('calendar.form.recurrence_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="select"
                        name="recurrenceType"
                        value={meetingForm.recurrenceType}
                        onChange={handleMeetingFormChange}
                        className="form-select"
                        disabled={meetingLoading}
                    >
                      <option value="One-Time">{t('calendar.form.recurrence_one_time')}</option>
                      <option value="Daily">{t('calendar.form.recurrence_daily')}</option>
                      <option value="Weekly">{t('calendar.form.recurrence_weekly')}</option>
                      <option value="Monthly">{t('calendar.form.recurrence_monthly')}</option>
                    </Input>
                  </Col>
                  {meetingForm.recurrenceType !== 'One-Time' && (
                      <>
                        <Col md={6} className="mb-3">
                          <label className="form-label mb-0" id="end-date-tooltip">
                            <i className="ri-calendar-line me-1"></i> {t('calendar.form.recurrence_end_date')}
                          </label>
                          <UncontrolledTooltip placement="top" target="end-date-tooltip">
                            {t('calendar.form.recurrence_end_date_tooltip')}
                          </UncontrolledTooltip>
                          <Flatpickr
                              className="form-control"
                              options={{
                                enableTime: true,
                                dateFormat: "Y-m-d H:i",
                                minDate: new Date(meetingForm.date).setDate(new Date(meetingForm.date).getDate() + 1),
                                defaultDate: meetingForm.endDate,
                              }}
                              value={meetingForm.endDate}
                              onChange={(date) => handleDateChange('endDate', date)}
                              disabled={meetingLoading}
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <label className="form-label" id="repeat-interval-tooltip">
                            <i className="ri-repeat-line me-1"></i> {t('calendar.form.repeat_interval')}
                          </label>
                          <UncontrolledTooltip placement="top" target="repeat-interval-tooltip">
                            {t('calendar.form.repeat_interval_tooltip')}
                          </UncontrolledTooltip>
                          <Input
                              type="number"
                              name="repeatInterval"
                              value={meetingForm.repeatInterval}
                              onChange={handleMeetingFormChange}
                              className="form-control"
                              min="1"
                              max={meetingForm.recurrenceType === 'Daily' ? 90 : meetingForm.recurrenceType === 'Weekly' ? 12 : 3}
                              disabled={meetingLoading}
                          />
                        </Col>
                        {meetingForm.recurrenceType === 'Weekly' && (
                            <Col md={6} className="mb-3">
                              <label className="form-label" id="weekly-days-tooltip">
                                <i className="ri-calendar-line me-1"></i> {t('calendar.form.weekly_days')}
                              </label>
                              <UncontrolledTooltip placement="top" target="weekly-days-tooltip">
                                {t('calendar.form.weekly_days_tooltip')}
                              </UncontrolledTooltip>
                              <Input
                                  type="select"
                                  name="weeklyDays"
                                  value={meetingForm.weeklyDays}
                                  onChange={handleMeetingFormChange}
                                  className="form-select"
                                  disabled={meetingLoading}
                              >
                                <option value="">{t('calendar.form.weekly_days_select')}</option>
                                <option value="1">{t('calendar.form.day_sunday')}</option>
                                <option value="2">{t('calendar.form.day_monday')}</option>
                                <option value="3">{t('calendar.form.day_tuesday')}</option>
                                <option value="4">{t('calendar.form.day_wednesday')}</option>
                                <option value="5">{t('calendar.form.day_thursday')}</option>
                                <option value="6">{t('calendar.form.day_friday')}</option>
                                <option value="7">{t('calendar.form.day_saturday')}</option>
                              </Input>
                            </Col>
                        )}
                        {meetingForm.recurrenceType === 'Monthly' && (
                            <Col md={6} className="mb-3">
                              <label className="form-label" id="monthly-day-tooltip">
                                <i className="ri-calendar-line me-1"></i> {t('calendar.form.monthly_day')}
                              </label>
                              <UncontrolledTooltip placement="top" target="monthly-day-tooltip">
                                {t('calendar.form.monthly_day_tooltip')}
                              </UncontrolledTooltip>
                              <Input
                                  type="number"
                                  name="monthlyDay"
                                  value={meetingForm.monthlyDay}
                                  onChange={handleMeetingFormChange}
                                  className="form-control"
                                  min="1"
                                  max="31"
                                  disabled={meetingLoading}
                              />
                            </Col>
                        )}
                      </>
                  )}
                  <Col md={6} className="mb-3">
                    <FormGroup check>
                      <Input
                          type="checkbox"
                          name="requireAuthentication"
                          checked={meetingForm.requireAuthentication}
                          onChange={handleMeetingFormChange}
                          disabled={meetingLoading}
                          id="auth-checkbox"
                      />
                      <Label check for="auth-checkbox" id="auth-tooltip">
                        <i className="ri-lock-line me-1"></i> {t('calendar.form.require_authentication')}
                      </Label>
                      <UncontrolledTooltip placement="top" target="auth-tooltip">
                        {t('calendar.form.require_authentication_tooltip')}
                      </UncontrolledTooltip>
                    </FormGroup>
                  </Col>
                  <Col md={6} className="mb-3">
                    <FormGroup check>
                      <Input
                          type="checkbox"
                          name="waitingRoom"
                          checked={meetingForm.waitingRoom}
                          onChange={handleMeetingFormChange}
                          disabled={meetingLoading}
                          id="waiting-room-checkbox"
                      />
                      <Label check for="waiting-room-checkbox" id="waiting-room-tooltip">
                        <i className="ri-door-lock-line me-1"></i> {t('calendar.form.waiting_room')}
                      </Label>
                      <UncontrolledTooltip placement="top" target="waiting-room-tooltip">
                        {t('calendar.form.waiting_room_tooltip')}
                      </UncontrolledTooltip>
                    </FormGroup>
                  </Col>
                  <Col md={12} className="mb-3">
                    <label className="form-label" id="participants-tooltip">
                      <i className="ri-user-line me-1"></i> {t('calendar.form.participants')}
                    </label>
                    <UncontrolledTooltip placement="top" target="participants-tooltip">
                      {t('calendar.form.participants_tooltip')}
                    </UncontrolledTooltip>
                    <Input
                        type="select"
                        name="participantIds"
                        multiple
                        value={meetingForm.participantIds}
                        onChange={(e) => setMeetingForm(prev => ({
                          ...prev,
                          participantIds: Array.from(e.target.selectedOptions, option => option.value),
                        }))}
                        className="form-select"
                        disabled={meetingLoading || !meetingForm.formationId}
                    >
                      {inscriptions.filter(ins => ins.formation._id === meetingForm.formationId).map(ins => (
                          <option key={ins._id} value={ins._id}>{ins.emailClient}</option>
                      ))}
                    </Input>
                  </Col>
                </Row>
                <div className="text-end mt-3">
                  <Button
                      color="danger"
                      className="me-2"
                      onClick={toggle}
                      disabled={meetingLoading}
                  >
                    {t('calendar.form.cancel')}
                  </Button>
                  <Button
                      color="success"
                      onClick={handleSubmitMeeting}
                      disabled={meetingLoading || !meetingForm.formationId || !meetingForm.hostEmail || !meetingForm.title || !meetingForm.duration ||
                          (meetingForm.recurrenceType !== 'One-Time' && (!meetingForm.endDate ||
                              (meetingForm.recurrenceType === 'Weekly' && !meetingForm.weeklyDays) ||
                              (meetingForm.recurrenceType === 'Monthly' && !meetingForm.monthlyDay)))}
                  >
                    {meetingLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t(`calendar.form.${isEdit ? 'updating' : 'creating'}`)}
                        </>
                    ) : (
                        t(`calendar.form.${isEdit ? 'update' : 'create'}`)
                    )}
                  </Button>
                </div>
              </ModalBody>
            </Modal>
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeleteMeeting}
                onCloseClick={() => setDeleteModal(false)}
            />
          </Container>
        </div>
      </React.Fragment>
  );
};

Calender.propTypes = {
  meetings: PropTypes.array,
  users: PropTypes.array,
  inscriptions: PropTypes.array,
};

export default Calender;